import { CheckAuth, getHeaders, getUserDetails, setCanonicalUserId } from 'auth/auth';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';

const isUuidLike = (value) => {
  if (!value) return false;
  return /^[0-9a-fA-F-]{36}$/.test(String(value));
};

const resolvePortalPrefix = () => {
  const cachedUser = CheckAuth() || {};
  const role = String(cachedUser.role || cachedUser.originalRole || '').toLowerCase();
  const userId = cachedUser.user_id;

  if (role === 'consumer' || isUuidLike(userId)) {
    return '/b2c';
  }

  return '/b2b';
};

const withPortalPrefix = (path = '') => {
  const prefix = resolvePortalPrefix();
  if (!path) return prefix;
  if (path.startsWith('/b2b') || path.startsWith('/b2c')) return path;
  if (path.startsWith('/')) return `${prefix}${path}`;
  return `${prefix}/${path}`;
};

export const onboardingAPI = createApi({
  reducerPath: 'onboarding',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['getOnboarding'],
  endpoints: (builder) => ({
    // generate application id after successful onboarding
    createApplication: builder.mutation({
      query: ({ data }) => {
        return {
          url: withPortalPrefix('/loan/application'),
          method: 'POST',
          headers: getHeaders(),
          body: data,
        };
      },
    }),
    createNewApplication: builder.mutation({
      query: ({ data }) => {
        return {
          url: withPortalPrefix('/loan/application/new'),
          method: 'POST',
          headers: getHeaders(),
          body: data,
        };
      },
    }),
    // post onboarding data to API
    createOnboarding: builder.mutation({
      query: ({ data }) => {
        return {
          url: withPortalPrefix('user/onboarding_profile'),
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getOnboarding'],
    }),
    // update/put onboarding data to API
    updateOnboarding: builder.mutation({
      query: ({ data }) => {
        return {
          url: withPortalPrefix('user/onboarding_profile'),
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getOnboarding'],
    }),
    // fetch onboarding data from API
    getOnboardingData: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('user/onboarding_profile/'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getOnboarding'],
    }),
    getOnboarding: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/loan/application_dropdowns/application_status'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    isFirstTimeUser: builder.mutation({
      query: () => {
        const { userId, userEmail, firstName, lastName } = getUserDetails();
        return {
          url: withPortalPrefix('/user/is_first_time_user'),
          method: 'POST',
          headers: getHeaders(),
          body: {
            user_id: String(userId),
            source: 'auth0',
            email: userEmail,
            first_name: firstName,
            last_name: lastName,
          },
        };
      },
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const resolvedUserId = data?.data?.user_id;
          if (resolvedUserId) {
            setCanonicalUserId(resolvedUserId);
          }
        } catch (error) {
          console.warn('Failed to cache canonical user id', error);
        }
      },
    }),
    //get list of loan officers
    getLoanOfficers: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('/user/loan_officers/'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    //get user details
    getUserInfo: builder.query({
      query: () => {
        const { userId } = getUserDetails();
        return {
          url: withPortalPrefix(`/user/user_profiles/${userId}`),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getLoanOfficerDetails: builder.query({
      query: () => {
        return {
          url: withPortalPrefix('user/get_loan_officer_details'),
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
  }),
});

export const {
  useCreateApplicationMutation,
  useCreateNewApplicationMutation,
  useGetLoanOfficersQuery,
  useIsFirstTimeUserMutation,
  useGetUserInfoQuery,
  useCreateOnboardingMutation,
  useUpdateOnboardingMutation,
  useLazyGetOnboardingQuery,
  useGetOnboardingDataQuery,
  useLazyGetOnboardingDataQuery,
  useGetLoanOfficerDetailsQuery,
} = onboardingAPI;
